import { CaretDownIcon } from "@phosphor-icons/react"
import { forwardRef, type ReactNode } from "react"

import { Button } from "~/components/reusable/Button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/reusable/DropdownMenu"
import { Tooltip } from "~/components/reusable/Tooltip"
import { usePermission, type PermissionRequirement } from "~/hooks/usePermission"
import { cn } from "~/utils/helpers"

type CommandbarOverflowItemProps = {
  icon?: ReactNode
  children: ReactNode
  /** Radix's select handler — fires for click and for keyboard Enter/Space alike. */
  onSelect?: () => void
  disabled?: boolean
  /** Why the row is disabled, shown on hover. The permission reason wins while blocked. */
  disabledReason?: string
  requires?: PermissionRequirement
  className?: string
}

/**
 * One row inside a {@link CommandbarOverflow}. A flat full-width row, not a pill — a menu of
 * bordered Buttons reads as floating chips rather than a list.
 *
 * A `DropdownMenuItem`, so Radix keeps arrow-key navigation, typeahead and focus management; the
 * ref and props forward to it, which is what lets a dialog slot this row as its `customTrigger`.
 * Approved surface: design-system/APPROVED.md.
 */
export const CommandbarOverflowItem = forwardRef<HTMLDivElement, CommandbarOverflowItemProps>(
  function CommandbarOverflowItem(
    { icon, children, onSelect, disabled, disabledReason, requires, className, ...rest },
    ref,
  ) {
    const { allowed, reason } = usePermission(requires)
    const isDisabled = disabled || !allowed
    const tooltip = allowed ? (disabled ? disabledReason : undefined) : reason
    const item = (
      <DropdownMenuItem
        ref={ref}
        disabled={isDisabled}
        onSelect={isDisabled ? undefined : onSelect}
        className={cn(
          "w-full gap-2 px-2 py-1 label-sm",
          // Icons vary in optical width; a fixed box keeps every label on the same x.
          "[&>svg]:size-3.5 [&>svg]:shrink-0",
          isDisabled && "cursor-not-allowed text-fg-disabled",
          className,
        )}
        {...rest}
      >
        {icon}
        <span className="truncate">{children}</span>
      </DropdownMenuItem>
    )
    // A disabled item takes no pointer events, so the tooltip hangs off a wrapper that still does.
    return tooltip ? (
      <Tooltip title={tooltip} side="left">
        <div>{item}</div>
      </Tooltip>
    ) : (
      item
    )
  },
)

type CommandbarOverflowProps = {
  children: ReactNode
  /** Trigger label. Figma's Commandbar spec calls this "More". */
  label?: string
  /** A bare "..." trigger, matching the Product Management command bar. Overrides `label`. */
  ellipsis?: boolean
}

/**
 * The overflow menu inside a Commandbar, holding bulk actions that don't earn a visible button.
 * Children are {@link CommandbarOverflowItem} rows, or dialog triggers slotting one in via asChild.
 * Canonical intent: {@link ../design-system/intents.ts COMMANDBAR_OVERFLOW_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
export default function CommandbarOverflow({
  children,
  label = "More",
  ellipsis,
}: CommandbarOverflowProps) {
  return (
    <DropdownMenu>
      {/* Gating belongs on each row, not here: the menu itself mutates nothing, and a disabled
          trigger would hide which actions a read-only user is missing. */}
      <DropdownMenuTrigger asChild>
        {ellipsis ? (
          <Button variant="neutral" size="sm" aria-label="More actions">
            ...
          </Button>
        ) : (
          <Button variant="neutral" size="xs">
            {label}
            <CaretDownIcon />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        // Sizes to the longest label rather than a guessed fixed width, but never narrower than
        // the trigger and never wide enough to look like a panel.
        className="flex w-max min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[280px] flex-col gap-0.5"
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
